import { Box } from "@mui/material";
import { generateElectricSparks, generateBurstSparks } from "./electricAnimations";
import { useEffect, useState } from "react";

const ElectricEffects = ({ 
  showSparks = true, 
  showBurst = true, 
  iconSize = "medium",
  isHovered = false 
}) => {
  // State to manage spark elements
  const [electricSparks, setElectricSparks] = useState([]);
  const [burstSparks, setBurstSparks] = useState([]);
  
  // Define size mapping
  const sizeMap = {
    small: {
      sparkSizeMultiplier: 0.8,
      dotSize: "2px"
    },
    medium: {
      sparkSizeMultiplier: 1,
      dotSize: "2px"
    },
    large: {
      sparkSizeMultiplier: 1.2,
      dotSize: "3px"
    }
  };
  
  const { sparkSizeMultiplier, dotSize } = sizeMap[iconSize] || sizeMap.medium;

  // Greenish colors from your theme
  const cyanColor = "hsl(var(--cyan))";
  const emeraldColor = "hsl(var(--emerald))";
  const lightCyan = "hsl(var(--cyan) / 0.7)";
  const lightEmerald = "hsl(var(--emerald) / 0.7)";

  // Generate or remove sparks based on hover state
  useEffect(() => {
    if (isHovered) {
      // Generate new sparks when hovered
      setElectricSparks(generateElectricSparks(6));
      setBurstSparks(generateBurstSparks(8));
    } else {
      // Clear sparks immediately when not hovered
      setElectricSparks([]);
      setBurstSparks([]);
    }
  }, [isHovered]);

  // If not hovered, don't render anything
  if (!isHovered) {
    return null;
  }

  return (
    <>
      {/* Greenish electric trail sparks - only rendered when hovered */}
      {showSparks && electricSparks.map((spark) => (
        <Box
          key={`spark-${spark.id}-${Date.now()}`}
          className="electric-spark"
          sx={{
            position: "absolute",
            width: `${spark.size * sparkSizeMultiplier}px`,
            height: `${spark.size * sparkSizeMultiplier}px`,
            background: spark.color,
            borderRadius: "50%",
            opacity: 0,
            zIndex: 1,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: `0 0 4px ${spark.color}, 0 0 8px ${spark.color}80`,
            filter: "blur(0.5px)",
            animation: `${spark.trail} 1.2s ease-out infinite`,
          }}
        />
      ))}
      
      {/* Greenish burst sparks - only rendered when hovered */}
      {showBurst && burstSparks.map((spark) => (
        <Box
          key={`burst-${spark.id}-${Date.now()}`}
          className="spark-burst"
          sx={{
            position: "absolute",
            width: `${spark.size * sparkSizeMultiplier}px`,
            height: `${spark.size * sparkSizeMultiplier}px`,
            background: spark.color,
            borderRadius: "50%",
            opacity: 0,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            "--angle": spark.angle,
            "--distance": spark.distance * 0.8,
            boxShadow: `0 0 3px ${spark.color}, 0 0 6px ${spark.color}80`,
            filter: "blur(0.3px)",
            animation: "spark-burst 0.8s ease-out infinite",
          }}
        />
      ))}
      
      {/* Subtle pulsing dots - only when hovered */}
      <>
        {/* Top dot - cyan */}
        <Box
          sx={{
            position: "absolute",
            width: dotSize,
            height: dotSize,
            background: cyanColor,
            borderRadius: "50%",
            top: "8px",
            left: "50%",
            transform: "translateX(-50%)",
            animation: "module-spark-fade 2s ease-in-out infinite",
            boxShadow: `0 0 3px ${cyanColor}, 0 0 6px ${lightCyan}`,
            zIndex: 1,
          }}
        />
        {/* Right dot - emerald */}
        <Box
          sx={{
            position: "absolute",
            width: dotSize,
            height: dotSize,
            background: emeraldColor,
            borderRadius: "50%",
            top: "50%",
            right: "8px",
            transform: "translateY(-50%)",
            animation: "module-spark-fade 2s ease-in-out infinite 0.5s",
            boxShadow: `0 0 3px ${emeraldColor}, 0 0 6px ${lightEmerald}`,
            zIndex: 1,
          }}
        />
        {/* Bottom dot - cyan */}
        <Box
          sx={{
            position: "absolute",
            width: dotSize,
            height: dotSize,
            background: cyanColor,
            borderRadius: "50%",
            bottom: "8px",
            left: "50%",
            transform: "translateX(-50%)",
            animation: "module-spark-fade 2s ease-in-out infinite 1s",
            boxShadow: `0 0 3px ${cyanColor}, 0 0 6px ${lightCyan}`,
            zIndex: 1,
          }}
        />
        {/* Left dot - emerald */}
        <Box
          sx={{
            position: "absolute",
            width: dotSize,
            height: dotSize,
            background: emeraldColor,
            borderRadius: "50%",
            top: "50%",
            left: "8px",
            transform: "translateY(-50%)",
            animation: "module-spark-fade 2s ease-in-out infinite 1.5s",
            boxShadow: `0 0 3px ${emeraldColor}, 0 0 6px ${lightEmerald}`,
            zIndex: 1,
          }}
        />
      </>
    </>
  );
};

export default ElectricEffects;